import React, {useContext, useEffect, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Stack, Typography, useTheme} from "@mui/material";
import {Delete, EditRounded} from "@mui/icons-material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import {useTranslation} from "react-i18next";
import {useFieldArray, useForm, FormProvider} from "react-hook-form";
import {AxiosError} from "axios";
import {useRecoilState} from "recoil";
import FormTextField from "../../../common/form-fields-wrappers/FormTextField";
import {generalInfoState, posInfoState} from "../../../search-dms/state";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {updatePosInfo} from "../../../search-dms/dmsSearch.actions";
import {SET_SNACKBAR} from "../../../common/utils";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {useHasScope} from "../../../../../config/globalConfig";
import {NetworkScope} from "../../../types";

const PosInfo = () => {
    const hasScope = useHasScope;
    const {t} = useTranslation(['network', 'translation']);
    const theme = useTheme();
    const [posInfo, setPosInfo] = useRecoilState(posInfoState);
    const [generalInfo,setGeneralInfo] = useRecoilState(generalInfoState);
    const [temp , setTemp] = useState(false);
    const [tempError , setTempError] = useState(false);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const posInfoForm = useForm({
        defaultValues: {
            posList: posInfo ?? [],
        },
        // resolver: yupResolver(
        //     yup.object({
        //         termId: yup.string().required('common.error.required').nullable()
        //     }))
    });

    const handleInvalidFields = (arr: any) => {
        const tempArray = arr.map((obj: any) => { return obj.termId });
        return tempArray.indexOf("") !== -1;
    }

    const handleControlID = (arr: any) =>{
        const tempArray = arr.map((obj: any) => { return obj.termId });
        const isDuplicate = tempArray.some((obj: any, index: any) => {
            return tempArray.indexOf(obj) !== index
        });
        return isDuplicate
    }

    const posArray = useFieldArray({ name: 'posList', control: posInfoForm.control});

    const onSubmit = (data: any) => {
        const reqParameters = {
            id: generalInfo.id,
            posInfo: data.posList
        }
        const isDuplicate = handleControlID(reqParameters.posInfo);
        const isIdNull = handleInvalidFields(reqParameters.posInfo);
        setTemp(handleControlID(reqParameters.posInfo));
        setTempError(handleInvalidFields(reqParameters.posInfo));
        if(isDuplicate || isIdNull){
            setOpen(false);
        }else{
            //setPosInfo(posInfo);
            updatePosInfo(reqParameters)
                .then((res: any) => {
                    setPosInfo(res.posInfo);
                    setIsDisabled(true);
                    setOpen(false);
                })
                .catch((err: AxiosError) => {
                    dispatchGlobalData(SET_SNACKBAR.ERROR);
                });
        }

    }
    const [isDisabled, setIsDisabled] = useState(true);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [open, setOpen] = useState(false);
    const handleDisableForm = (value: boolean) => {
        setIsDisabled(value);
    }

    const onError = (errors: any) => {
        setOpen(false);
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('translation:error.tryLater'),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const handleAddColumn = () => {
        posArray.append({
            termId: "",
            posteOperatorCode: "",
            amexOperatorCode: ""
        })
    }

    const handleOnDelete = ( index?: number) => {
        posArray.remove(index)
    }

    useEffect(() => {
        posInfoForm.setFocus("posList");
        posInfoForm.setValue("posList", posInfo);
    }, [posInfo])

    return (
        <>
            <FormProvider {...posInfoForm}>
                <Grid container direction="row" alignItems="start">
                    <Grid item xs={12}>
                        <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                            {hasScope(NetworkScope.MsMng) && isDisabled &&
                                <IconButton color={"accent"} onClick={() => {handleDisableForm(false)}}>
                                    <EditRounded/>
                                </IconButton>
                            }
                        </Stack>
                    </Grid>

                    {!isDisabled &&
                        <Grid item container xs={12} md={12} justifyContent="flex-end" alignItems="flex-end">
                            <Box sx={{my: theme.spacing(2)}}>
                                <Button variant={'text'} color={"accent"} onClick={handleAddColumn}>
                                    {t('dmsDetail.posInfo.button.addPos')}
                                </Button>
                            </Box>
                        </Grid>
                    }

                    {posArray.fields.length === 0 &&
                        <Grid item container xs={12} md={12} align-items="center" justifyContent="center">
                            <p>{t('dmsDetail.posInfo.message.info.posEmptyState')}</p>
                        </Grid>
                    }

                    <Grid item container xs={12} md={12} >
                        {temp === true &&
                            <Grid item container xs={12} md={12}>
                                <Typography sx={{color: theme.palette.error.main}}>
                                    {t('dmsDetail.posInfo.message.info.duplicateTermId')}
                                </Typography>
                            </Grid>
                        }
                        {tempError === true &&
                            <Grid item container xs={12} md={12}>
                                <Typography sx={{color: theme.palette.error.main}}>
                                    {t('dmsDetail.posInfo.message.info.insertTermId')}
                                </Typography>
                            </Grid>
                        }

                        {posArray.fields.map((field, index) => {
                            return (
                                <Grid key={field.id} item container xs={12} md={12} columnSpacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <FormTextField
                                            disabled={isDisabled}
                                            control={posInfoForm.control}
                                            name={`posList.${index}.termId`}
                                            //name={`chargingColumn.${index}.chargingColumnType`}
                                            label={t('dmsDetail.posInfo.form.label.termId')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormTextField
                                            disabled={isDisabled}
                                            control={posInfoForm.control}
                                            name={`posList.${index}.posteOperatorCode`}
                                            label={t('dmsDetail.posInfo.form.label.posteOperatorCode')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormTextField
                                            disabled={isDisabled}
                                            control={posInfoForm.control}
                                            name={`posList.${index}.amexOperatorCode`}
                                            label={t('dmsDetail.posInfo.form.label.amexOperatorCode')}
                                        />
                                    </Grid>
                                    {
                                        isDisabled === false &&
                                        <IconButton sx={{
                                            mt: theme.spacing(2),
                                            ml: 'auto',
                                            mr: theme.spacing(2)
                                        }}
                                                    onClick={() => {
                                                        handleOnDelete(index)
                                                    }}>
                                            <Delete/>
                                        </IconButton>
                                     }
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Grid container item direction="row" xs={12} columnSpacing={2}>
                        <Grid item xs={12}>
                            <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                {!isDisabled &&
                                    <Button variant="contained" onClick={handleOpen}>
                                        {t("dmsDetail.posInfo.button.edit")}
                                    </Button>
                                }
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </FormProvider>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t("dmsDetail.posInfo.dialog.title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("dmsDetail.posInfo.dialog.content")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant={"outlined"} color={"accent"}>
                        {t("dmsDetail.posInfo.dialog.action.cancel")}
                    </Button>
                    <Button onClick={posInfoForm.handleSubmit(onSubmit, onError)} autoFocus>
                        {t("dmsDetail.posInfo.dialog.action.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default PosInfo;